import type { Punto, Anillo, Poligono, MultiPoligono, Bbox } from './geometria';
import { bboxMultiPoligono, puntoEnMultiPoligono } from './geometria';
import { distanciaPuntoSegmento } from './distancias';

/**
 * Recortes en planta sin dependencias: distancia de un punto a un poligono (0 dentro), recorte
 * de polilineas por esa distancia (curvas del modelo LiDAR con el desfase de la parcela) y
 * recorte de poligonos y polilineas por un rectangulo (Sutherland-Hodgman y Liang-Barsky) para
 * el DXF de una zona dibujada.
 */

/** Lee un GeoJSON Polygon/MultiPolygon (texto u objeto) como MultiPoligono. Devuelve [] si no se entiende. */
export function multiPoligonoDeGeoJson(geom: string | { type: string; coordinates: unknown } | null | undefined): MultiPoligono {
  if (!geom) return [];
  try {
    const g = typeof geom === 'string' ? (JSON.parse(geom) as { type: string; coordinates: unknown }) : geom;
    if (g.type === 'MultiPolygon') return g.coordinates as MultiPoligono;
    if (g.type === 'Polygon') return [g.coordinates as Poligono];
    if (g.type === 'Feature') return multiPoligonoDeGeoJson((g as unknown as { geometry: { type: string; coordinates: unknown } }).geometry);
  } catch { /* geometria ilegible */ }
  return [];
}

/**
 * Distancia de puntos a un multipoligono (0 si el punto esta dentro, fuera de los huecos). Guarda
 * los segmentos en arrays planos; para las parcelas (decenas o cientos de lados) basta con
 * recorrerlos todos, con un descarte previo por la caja.
 */
export class DistanciaPoligono {
  private readonly ax: Float64Array;
  private readonly ay: Float64Array;
  private readonly bx: Float64Array;
  private readonly by: Float64Array;
  readonly caja: Bbox;

  constructor(readonly mp: MultiPoligono) {
    const segs: number[] = [];
    for (const pol of mp) {
      for (const an of pol) {
        for (let i = 0; i < an.length; i++) {
          const a = an[i], b = an[(i + 1) % an.length];
          if (a[0] === b[0] && a[1] === b[1]) continue;
          segs.push(a[0], a[1], b[0], b[1]);
        }
      }
    }
    const n = segs.length / 4;
    this.ax = new Float64Array(n); this.ay = new Float64Array(n); this.bx = new Float64Array(n); this.by = new Float64Array(n);
    for (let i = 0; i < n; i++) { this.ax[i] = segs[i * 4]; this.ay[i] = segs[i * 4 + 1]; this.bx[i] = segs[i * 4 + 2]; this.by[i] = segs[i * 4 + 3]; }
    this.caja = mp.length ? bboxMultiPoligono(mp) : [0, 0, 0, 0];
  }

  get vacio(): boolean {
    return this.ax.length === 0;
  }

  distancia(p: Punto): number {
    if (this.vacio) return Infinity;
    const [x, y] = p;
    const c = this.caja;
    if (x >= c[0] && x <= c[2] && y >= c[1] && y <= c[3] && puntoEnMultiPoligono(p, this.mp)) return 0;
    let mejor = Infinity;
    for (let i = 0; i < this.ax.length; i++) {
      const d = distanciaPuntoSegmento(p, [this.ax[i], this.ay[i]], [this.bx[i], this.by[i]]);
      if (d < mejor) mejor = d;
    }
    return mejor;
  }
}

export function distanciaAPoligono(p: Punto, mp: MultiPoligono): number {
  return new DistanciaPoligono(mp).distancia(p);
}

/**
 * Parte una polilinea en los tramos cuyos vertices estan a <= `maximo`. Con `interpolar`, cada
 * tramo se prolonga hasta el punto del segmento donde la distancia (interpolada linealmente entre
 * sus extremos) vale `maximo`; sin el, el corte es por vertice. Los tramos de un solo punto se
 * descartan.
 */
export function tramosPorDistancia<T>(puntos: T[], distancias: ArrayLike<number>, maximo: number, interpolar?: (a: T, b: T, t: number) => T): T[][] {
  const salida: T[][] = [];
  let actual: T[] = [];
  const lim = maximo + 1e-9;
  for (let i = 0; i < puntos.length; i++) {
    const dentro = distancias[i] <= lim;
    const previoDentro = i > 0 && distancias[i - 1] <= lim;
    if (dentro) {
      if (i > 0 && !previoDentro && interpolar) {
        const t = (distancias[i - 1] - maximo) / (distancias[i - 1] - distancias[i]);
        if (t < 1 - 1e-9) actual.push(interpolar(puntos[i - 1], puntos[i], t));
      }
      actual.push(puntos[i]);
    } else {
      if (previoDentro && interpolar) {
        const t = (maximo - distancias[i - 1]) / (distancias[i] - distancias[i - 1]);
        if (t > 1e-9) actual.push(interpolar(puntos[i - 1], puntos[i], t));
      }
      if (actual.length > 1) salida.push(actual);
      actual = [];
    }
  }
  if (actual.length > 1) salida.push(actual);
  return salida;
}

/** Interpolacion lineal de vertices [x, y, ...] para `tramosPorDistancia`. */
export function interpolarVertice(a: number[], b: number[], t: number): number[] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

// ---------- Recorte por un rectangulo ----------

/** Sutherland-Hodgman de un anillo contra un rectangulo. Devuelve el anillo cerrado (primer = ultimo) o [] si queda vacio. */
export function recortarAnilloRect(anillo: Anillo, b: Bbox): Anillo {
  let pts: Punto[] = anillo.slice();
  if (pts.length > 1 && pts[0][0] === pts[pts.length - 1][0] && pts[0][1] === pts[pts.length - 1][1]) pts.pop();
  const bordes: { dentro: (p: Punto) => boolean; corte: (a: Punto, c: Punto) => Punto }[] = [
    { dentro: (p) => p[0] >= b[0], corte: (a, c) => [b[0], a[1] + ((c[1] - a[1]) * (b[0] - a[0])) / (c[0] - a[0])] },
    { dentro: (p) => p[0] <= b[2], corte: (a, c) => [b[2], a[1] + ((c[1] - a[1]) * (b[2] - a[0])) / (c[0] - a[0])] },
    { dentro: (p) => p[1] >= b[1], corte: (a, c) => [a[0] + ((c[0] - a[0]) * (b[1] - a[1])) / (c[1] - a[1]), b[1]] },
    { dentro: (p) => p[1] <= b[3], corte: (a, c) => [a[0] + ((c[0] - a[0]) * (b[3] - a[1])) / (c[1] - a[1]), b[3]] },
  ];
  for (const borde of bordes) {
    if (pts.length === 0) break;
    const entrada = pts;
    pts = [];
    for (let i = 0; i < entrada.length; i++) {
      const actual = entrada[i], previo = entrada[(i + entrada.length - 1) % entrada.length];
      const dActual = borde.dentro(actual), dPrevio = borde.dentro(previo);
      if (dActual) {
        if (!dPrevio) pts.push(borde.corte(previo, actual));
        pts.push(actual);
      } else if (dPrevio) {
        pts.push(borde.corte(previo, actual));
      }
    }
  }
  if (pts.length < 3) return [];
  return [...pts, [pts[0][0], pts[0][1]]];
}

/** Recorta cada poligono (exterior y huecos) por el rectangulo; descarta los que quedan vacios. */
export function recortarMultiPoligonoRect(mp: MultiPoligono, b: Bbox): MultiPoligono {
  const salida: MultiPoligono = [];
  for (const pol of mp) {
    if (!pol.length) continue;
    const exterior = recortarAnilloRect(pol[0], b);
    if (!exterior.length) continue;
    const huecos = pol.slice(1).map((h) => recortarAnilloRect(h, b)).filter((h) => h.length > 0);
    salida.push([exterior, ...huecos]);
  }
  return salida;
}

/** Liang-Barsky de un segmento: devuelve el tramo interior o null. */
function recortarSegmento(a: Punto, c: Punto, b: Bbox): [Punto, Punto] | null {
  const dx = c[0] - a[0], dy = c[1] - a[1];
  let t0 = 0, t1 = 1;
  const pruebas: [number, number][] = [[-dx, a[0] - b[0]], [dx, b[2] - a[0]], [-dy, a[1] - b[1]], [dy, b[3] - a[1]]];
  for (const [p, q] of pruebas) {
    if (p === 0) {
      if (q < 0) return null;
    } else {
      const r = q / p;
      if (p < 0) { if (r > t1) return null; if (r > t0) t0 = r; } else { if (r < t0) return null; if (r < t1) t1 = r; }
    }
  }
  return [[a[0] + t0 * dx, a[1] + t0 * dy], [a[0] + t1 * dx, a[1] + t1 * dy]];
}

/** Recorta una polilinea por el rectangulo en uno o varios tramos. */
export function recortarPolilineaRect(linea: Punto[], b: Bbox): Punto[][] {
  const salida: Punto[][] = [];
  let actual: Punto[] = [];
  const mismo = (p: Punto, q: Punto) => Math.abs(p[0] - q[0]) < 1e-9 && Math.abs(p[1] - q[1]) < 1e-9;
  for (let i = 1; i < linea.length; i++) {
    const s = recortarSegmento(linea[i - 1], linea[i], b);
    if (!s) {
      if (actual.length > 1) salida.push(actual);
      actual = [];
      continue;
    }
    if (actual.length && mismo(actual[actual.length - 1], s[0])) actual.push(s[1]);
    else {
      if (actual.length > 1) salida.push(actual);
      actual = [s[0], s[1]];
    }
    // Si el segmento salio del rectangulo, el tramo se cierra aqui.
    if (!mismo(s[1], linea[i])) {
      salida.push(actual);
      actual = [];
    }
  }
  if (actual.length > 1) salida.push(actual);
  return salida;
}
